import React, { useState, useEffect } from 'react';
import { nexiGlobalTemplateService } from '../../services/api';
import { NexiGlobalTemplateType, LanguageCode } from '../../utils/constants';

const NexiGlobalTemplateSection = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);

  // Form states
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState(LanguageCode.EN_US);
  const [previewContent, setPreviewContent] = useState('');
  const [previewVariables, setPreviewVariables] = useState({});
  const [formData, setFormData] = useState({
    id: '',
    description: '',
    type: NexiGlobalTemplateType.GENERAL,
    content: {
      [LanguageCode.EN_US]: '',
      [LanguageCode.ZH_HK]: '',
      [LanguageCode.ZH_CN]: ''
    }
  });

  // Load templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Filter templates when searchTerm or templates change
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTemplates(templates);
    } else {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const filtered = templates.filter(
        (template) =>
          template.id.toLowerCase().includes(lowerCaseSearchTerm) ||
          template.description.toLowerCase().includes(lowerCaseSearchTerm) ||
          getTemplateTypeName(template.type).toLowerCase().includes(lowerCaseSearchTerm)
      );
      setFilteredTemplates(filtered);
    }
  }, [searchTerm, templates]);

  // Fetch all templates
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await nexiGlobalTemplateService.getAllTemplates();
      setTemplates(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('content.')) {
      const language = name.split('.')[1];
      setFormData({
        ...formData,
        content: {
          ...formData.content,
          [language]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Handle preview variable input changes
  const handleVariableInputChange = (e) => {
    const { name, value } = e.target;
    setPreviewVariables({
      ...previewVariables,
      [name]: value
    });
  };

  // Handle dialog open/close
  const handleAddDialogOpen = () => {
    setFormData({
      id: '',
      description: '',
      type: NexiGlobalTemplateType.GENERAL,
      content: {
        [LanguageCode.EN_US]: '',
        [LanguageCode.ZH_HK]: '',
        [LanguageCode.ZH_CN]: ''
      }
    });
    setActiveTab(0);
    setOpenAddDialog(true);
  };

  const handleEditDialogOpen = (template) => {
    setSelectedTemplate(template);
    setFormData({
      id: template.id,
      description: template.description,
      type: template.type,
      content: {
        [LanguageCode.EN_US]: template.content[LanguageCode.EN_US] || '',
        [LanguageCode.ZH_HK]: template.content[LanguageCode.ZH_HK] || '',
        [LanguageCode.ZH_CN]: template.content[LanguageCode.ZH_CN] || ''
      }
    });
    setActiveTab(0);
    setOpenEditDialog(true);
  };

  const handlePreviewDialogOpen = (template) => {
    setSelectedTemplate(template);
    setSelectedLanguage(LanguageCode.EN_US);
    setPreviewContent(template.content[LanguageCode.EN_US] || '');
    setPreviewVariables({});
    setOpenPreviewDialog(true);
  };

  const handlePreviewLanguageChange = (e) => {
    const language = e.target.value;
    setSelectedLanguage(language);
    setPreviewContent(selectedTemplate.content[language] || '');
  };

  const handlePreviewRender = () => {
    let renderedContent = selectedTemplate.content[selectedLanguage] || '';
    Object.entries(previewVariables).forEach(([variable, value]) => {
      const regex = new RegExp(`{{${variable}}}`, 'g');
      renderedContent = renderedContent.replace(regex, value);
    });
    setPreviewContent(renderedContent);
  };

  const handleDeleteDialogOpen = (template) => {
    setSelectedTemplate(template);
    setOpenDeleteDialog(true);
  };

  const handleLanguageTabChange = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  const handleCloseDialogs = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setOpenPreviewDialog(false);
    setSelectedTemplate(null);
  };

  // CRUD operations
  const handleAddTemplate = async () => {
    try {
      await nexiGlobalTemplateService.createTemplate(formData);
      handleCloseDialogs();
      fetchTemplates();
    } catch (err) {
      console.error('Error adding template:', err);
      setError('Failed to add template. Please try again.');
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      await nexiGlobalTemplateService.updateTemplate(formData.id, formData);
      handleCloseDialogs();
      fetchTemplates();
    } catch (err) {
      console.error('Error updating template:', err);
      setError('Failed to update template. Please try again.');
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      await nexiGlobalTemplateService.deleteTemplate(selectedTemplate.id);
      handleCloseDialogs();
      fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template. Please try again.');
    }
  };

  // Get template type display name
  const getTemplateTypeName = (typeValue) => {
    const type = Object.entries(NexiGlobalTemplateType).find(([_, value]) => value === typeValue);
    return type ? type[0].replace('_', ' ') : typeValue;
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Nexi Global Templates</h2>
        <button
          className="bg-[#075e54] hover:bg-[#054c44] text-white px-4 py-2 rounded flex items-center space-x-2"
          onClick={handleAddDialogOpen}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          <span>Add Template</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4 relative">
        <div className="flex">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search templates by ID, description, or type..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full p-2 pl-10 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-2.5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-gray-600">
            Found {filteredTemplates.length} {filteredTemplates.length === 1 ? 'template' : 'templates'} matching "{searchTerm}"
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#075e54]"></div>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="bg-gray-50 p-8 text-center rounded">
          <p className="text-gray-500">
            {templates.length === 0
              ? "No templates found. Add one to get started."
              : "No templates match your search. Try a different search term."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Languages</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTemplates.map((template) => (
                <tr key={template.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{template.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{template.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{getTemplateTypeName(template.type)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-1">
                      {template.content?.[LanguageCode.EN_US] && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          EN
                        </span>
                      )}
                      {template.content?.[LanguageCode.ZH_HK] && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          HK
                        </span>
                      )}
                      {template.content?.[LanguageCode.ZH_CN] && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                          CN
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2 justify-end">
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => handlePreviewDialogOpen(template)}
                        title="Preview"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <button
                        className="text-indigo-600 hover:text-indigo-900"
                        onClick={() => handleEditDialogOpen(template)}
                        title="Edit"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button
                        className="text-red-600 hover:text-red-900"
                        onClick={() => handleDeleteDialogOpen(template)}
                        title="Delete"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Dialog */}
      {openAddDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Add New Template</h3>
            <p className="text-gray-600 mb-4">Enter the details for the new global template.</p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Template ID</label>
              <input
                type="text"
                name="id"
                value={formData.id}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                placeholder="Enter template ID"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                placeholder="Enter description"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              >
                {Object.entries(NexiGlobalTemplateType).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-6 mb-2">
              <h4 className="font-medium text-gray-900">Template Content</h4>
            </div>

            <div className="border-b border-gray-200 mb-4">
              <nav className="flex -mb-px">
                <button
                  onClick={() => handleLanguageTabChange(0)}
                  className={`px-4 py-2 text-sm font-medium ${
                    activeTab === 0
                      ? 'border-b-2 border-[#075e54] text-[#075e54]'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  type="button"
                >
                  English
                </button>
                <button
                  onClick={() => handleLanguageTabChange(1)}
                  className={`px-4 py-2 text-sm font-medium ${
                    activeTab === 1
                      ? 'border-b-2 border-[#075e54] text-[#075e54]'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  type="button"
                >
                  Chinese (HK)
                </button>
                <button
                  onClick={() => handleLanguageTabChange(2)}
                  className={`px-4 py-2 text-sm font-medium ${
                    activeTab === 2
                      ? 'border-b-2 border-[#075e54] text-[#075e54]'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  type="button"
                >
                  Chinese (CN)
                </button>
              </nav>
            </div>

            <div className={activeTab === 0 ? 'block' : 'hidden'}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">English Content</label>
                <textarea
                  name={`content.${LanguageCode.EN_US}`}
                  value={formData.content[LanguageCode.EN_US]}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                ></textarea>
              </div>
            </div>

            <div className={activeTab === 1 ? 'block' : 'hidden'}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Chinese (HK) Content</label>
                <textarea
                  name={`content.${LanguageCode.ZH_HK}`}
                  value={formData.content[LanguageCode.ZH_HK]}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                ></textarea>
              </div>
            </div>

            <div className={activeTab === 2 ? 'block' : 'hidden'}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Chinese (CN) Content</label>
                <textarea
                  name={`content.${LanguageCode.ZH_CN}`}
                  value={formData.content[LanguageCode.ZH_CN]}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                ></textarea>
              </div>
            </div>

            <p className="text-xs text-gray-600 mt-2 mb-4">
              Use {'{{'}{'{variable}'}{'}}'} syntax for dynamic content. Example: "Hello {'{{'}{'{name}'}{'}}'}"
            </p>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCloseDialogs}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTemplate}
                className="px-4 py-2 bg-[#075e54] text-white rounded hover:bg-[#054c44]"
                type="button"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      {openEditDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit Template</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Template ID</label>
              <input
                type="text"
                name="id"
                value={formData.id}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              >
                {Object.entries(NexiGlobalTemplateType).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-6 mb-2">
              <h4 className="font-medium text-gray-900">Template Content</h4>
            </div>

            <div className="border-b border-gray-200 mb-4">
              <nav className="flex -mb-px">
                <button
                  onClick={() => handleLanguageTabChange(0)}
                  className={`px-4 py-2 text-sm font-medium ${
                    activeTab === 0
                      ? 'border-b-2 border-[#075e54] text-[#075e54]'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  type="button"
                >
                  English
                </button>
                <button
                  onClick={() => handleLanguageTabChange(1)}
                  className={`px-4 py-2 text-sm font-medium ${
                    activeTab === 1
                      ? 'border-b-2 border-[#075e54] text-[#075e54]'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  type="button"
                >
                  Chinese (HK)
                </button>
                <button
                  onClick={() => handleLanguageTabChange(2)}
                  className={`px-4 py-2 text-sm font-medium ${
                    activeTab === 2
                      ? 'border-b-2 border-[#075e54] text-[#075e54]'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  type="button"
                >
                  Chinese (CN)
                </button>
              </nav>
            </div>

            <div className={activeTab === 0 ? 'block' : 'hidden'}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">English Content</label>
                <textarea
                  name={`content.${LanguageCode.EN_US}`}
                  value={formData.content[LanguageCode.EN_US]}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                ></textarea>
              </div>
            </div>

            <div className={activeTab === 1 ? 'block' : 'hidden'}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Chinese (HK) Content</label>
                <textarea
                  name={`content.${LanguageCode.ZH_HK}`}
                  value={formData.content[LanguageCode.ZH_HK]}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                ></textarea>
              </div>
            </div>

            <div className={activeTab === 2 ? 'block' : 'hidden'}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Chinese (CN) Content</label>
                <textarea
                  name={`content.${LanguageCode.ZH_CN}`}
                  value={formData.content[LanguageCode.ZH_CN]}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                ></textarea>
              </div>
            </div>

            <p className="text-xs text-gray-600 mt-2 mb-4">
              Use {'{{'}{'{variable}'}{'}}'} syntax for dynamic content. Example: "Hello {'{{'}{'{name}'}{'}}'}"
            </p>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCloseDialogs}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTemplate}
                className="px-4 py-2 bg-[#075e54] text-white rounded hover:bg-[#054c44]"
                type="button"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {openDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Template</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete the template "{selectedTemplate?.description}" (ID: {selectedTemplate?.id})? This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCloseDialogs}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTemplate}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                type="button"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      {openPreviewDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Preview Template</h3>

            <div className="mb-4">
              <p className="font-medium">{selectedTemplate?.description}</p>
              <p className="text-xs text-gray-500">ID: {selectedTemplate?.id}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
              <select
                value={selectedLanguage}
                onChange={handlePreviewLanguageChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              >
                {Object.entries(LanguageCode).map(([key, value]) => (
                  selectedTemplate?.content?.[value] ? (
                    <option key={value} value={value}>
                      {key === 'EN_US' ? 'English' : key === 'ZH_HK' ? 'Chinese (HK)' : 'Chinese (CN)'}
                    </option>
                  ) : null
                ))}
              </select>
            </div>

            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded">
              <p className="whitespace-pre-wrap">{previewContent}</p>
            </div>

            <div className="mb-4 border border-gray-200 rounded">
              <div
                className="p-4 flex items-center justify-between cursor-pointer"
                onClick={() => document.getElementById('variables-panel')?.classList.toggle('hidden')}
              >
                <h4 className="font-medium">Replace Variables</h4>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>

              <div id="variables-panel" className="px-4 pb-4 hidden">
                <p className="text-xs text-gray-600 mb-3">
                  Add variables to replace in the template. Variables follow the format {'{{'}{'{variable_name}'}{'}}'}.
                </p>

                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    id="variable-name-input"
                    className="flex-1 p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                    placeholder="Variable name (e.g. name)"
                  />
                  <input
                    type="text"
                    id="variable-value-input"
                    className="flex-1 p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                    placeholder="Value (e.g. John)"
                  />
                  <button
                    className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                    onClick={() => {
                      const nameInput = document.getElementById('variable-name-input');
                      const valueInput = document.getElementById('variable-value-input');
                      if (nameInput.value && valueInput.value) {
                        handleVariableInputChange({
                          target: { name: nameInput.value, value: valueInput.value }
                        });
                        nameInput.value = '';
                        valueInput.value = '';
                      }
                    }}
                  >
                    Add
                  </button>
                </div>

                <div className="flex flex-wrap gap-1 mb-4">
                  {Object.entries(previewVariables).map(([variableName, variableValue]) => (
                    <span
                      key={variableName}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {variableName}: {variableValue}
                      <button
                        className="ml-1 focus:outline-none"
                        onClick={() => {
                          const newVariables = { ...previewVariables };
                          delete newVariables[variableName];
                          setPreviewVariables(newVariables);
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <button
                    className="px-4 py-2 bg-[#075e54] text-white rounded hover:bg-[#054c44] disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handlePreviewRender}
                    disabled={Object.keys(previewVariables).length === 0}
                  >
                    Apply Variables
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={handleCloseDialogs}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NexiGlobalTemplateSection;
